/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { ErrorLogDetailDialogComponent } from './error-log-detail-dialog.component';

export class ErrorLogEntry {
    teamId: string | null;
    userId: string | null;
    userName: string | null;
    logDate: string;
    pathName: string | null;
    fullPath: string | null;
    relatedId: string | null;
    relatedName: string | null;
    message: string | null;
    stackTrace: string | null;
}

interface DateCount {
    date: string;
    count: number;
}

interface NameCount {
    name: string;
    count: number;
}

interface MessageCount {
    message: string;
    label: string;
    count: number;
}

const UNKNOWN_USER = '(unknown)';
const NO_MESSAGE = '(no message)';
const TOP_N = 5;

export interface ErrorLogsDialogData {
    errors: ErrorLogEntry[];
}

@Component({
    selector: 'app-error-logs-dialog',
    templateUrl: './error-logs-dialog.component.html',
    styleUrls: ['./error-logs-dialog.component.scss'],
    imports: [
        DialogBannerComponent, MatDialogContent, MatIconModule, MatTooltipModule,
        MatFormFieldModule, MatSelectModule, MatOptionModule
    ]
})
export class ErrorLogsDialogComponent {
    dialogRef = inject<MatDialogRef<ErrorLogsDialogComponent>>(MatDialogRef);
    private dialog = inject(MatDialog);
    private isNarrowService = inject(IsNarrowService);
    data = inject<ErrorLogsDialogData>(MAT_DIALOG_DATA);

    errors: ErrorLogEntry[] = this.data.errors ?? [];
    dateCounts: DateCount[] = this.buildDateCounts();
    readonly todayDate: string = this.todayIso();

    isPhone = false;

    selectedDate: string | null = null;
    selectedUser: string | null = null;
    selectedMessage: string | null = null;

    constructor() {
        this.isNarrowService.detectVeryNarrow().pipe(takeUntilDestroyed()).subscribe(result => { this.isPhone = result; });
        if (this.isNarrowService.isPhone()) {
            this.selectedDate = this.todayDate;
        }
    }

    private todayIso(): string {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const d = String(now.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    private buildDateCounts(): DateCount[] {
        const counts = new Map<string, number>();
        this.errors.forEach(e => {
            const date = this.dateOf(e);
            counts.set(date, (counts.get(date) ?? 0) + 1);
        });
        return Array.from(counts.entries())
            .map(([date, count]) => ({ date, count }))
            .sort((a, b) => b.date.localeCompare(a.date));
    }

    dateOf(error: ErrorLogEntry): string {
        return (error.logDate ?? '').substring(0, 10);
    }

    /** Just the time portion - used in the table once a date filter makes the date itself redundant. */
    timeOf(error: ErrorLogEntry): string {
        return (error.logDate ?? '').substring(11);
    }

    get topDates(): DateCount[] {
        return this.dateCounts.slice(0, 7);
    }

    /** Same as dateCounts, but guarantees today is present (0 count) so mobile users can select it and see the empty state. */
    get mobileDateOptions(): DateCount[] {
        if (this.dateCounts.some(d => d.date === this.todayDate)) {
            return this.dateCounts;
        }
        return [{ date: this.todayDate, count: 0 }, ...this.dateCounts];
    }

    get isTodayEmpty(): boolean {
        return this.isPhone && this.selectedDate === this.todayDate && this.dateFilteredErrors.length === 0;
    }

    /** "Wed 29/7" - parsed from the y-m-d components (not `new Date(str)`) so no UTC/local shift risk. */
    formatDateLabel(date: string): string {
        const [y, m, d] = date.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        const weekday = dt.toLocaleDateString('en-GB', { weekday: 'short' });
        return `${weekday} ${dt.getDate()}/${dt.getMonth() + 1}`;
    }

    /** Everything scoped to the selected day only - drives the Users/Errors panels so they stay in step with the day. */
    get dateFilteredErrors(): ErrorLogEntry[] {
        if (!this.selectedDate) {
            return this.errors;
        }
        return this.errors.filter(e => this.dateOf(e) === this.selectedDate);
    }

    get topUsers(): NameCount[] {
        return this.topCounts(this.dateFilteredErrors, e => e.userName || UNKNOWN_USER);
    }

    get topMessages(): MessageCount[] {
        const counts = new Map<string, number>();
        this.dateFilteredErrors.forEach(e => {
            const key = e.message || NO_MESSAGE;
            counts.set(key, (counts.get(key) ?? 0) + 1);
        });
        return Array.from(counts.entries())
            .map(([message, count]) => ({ message, label: this.truncateMessage(message), count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, TOP_N);
    }

    private topCounts(list: ErrorLogEntry[], keyFn: (e: ErrorLogEntry) => string): NameCount[] {
        const counts = new Map<string, number>();
        list.forEach(e => {
            const key = keyFn(e);
            counts.set(key, (counts.get(key) ?? 0) + 1);
        });
        return Array.from(counts.entries())
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, TOP_N);
    }

    private truncateMessage(message: string): string {
        return message.length > 10 ? message.substring(0, 10) + '…' : message;
    }

    get filteredErrors(): ErrorLogEntry[] {
        return this.dateFilteredErrors.filter(e =>
            (!this.selectedUser || (e.userName || UNKNOWN_USER) === this.selectedUser)
            && (!this.selectedMessage || (e.message || NO_MESSAGE) === this.selectedMessage)
        );
    }

    selectDate(date: string | null) {
        this.selectedDate = date;
        this.selectedUser = null;
        this.selectedMessage = null;
    }

    selectUser(name: string) {
        this.selectedUser = this.selectedUser === name ? null : name;
    }

    selectMessage(message: string) {
        this.selectedMessage = this.selectedMessage === message ? null : message;
    }

    /** Explicit setter for the mobile Users select, which always has a value (including "All" as null). */
    setUser(name: string | null) {
        this.selectedUser = name;
    }

    showDetail(error: ErrorLogEntry) {
        this.dialog.open(ErrorLogDetailDialogComponent, {
            data: { error },
            width: '90vw',
            maxWidth: '1000px',
        });
    }
}
